import { useState, useEffect, useCallback } from 'react';
import { MatriculaFormState, MATRICULA_INITIAL, MatriculaResponse } from '../types/matricula.types';
import { AñoEscolar, Grado, Seccion } from '../types/usuario.types';
import { TipoDescuento, DescuentoEstudiante } from '../types/pago.types';
import { matriculaService } from '../services/matriculaService';
import { catalogoService } from '../services/usuarioService';
import { tipoDescuentoService, descuentoEstudianteService } from '../services/pagoService';

export interface MatriculaError { [field: string]: string }

export interface TarifaForm {
  costoMatricula: string;
  costoPension:   string;
}

export const useMatricula = (estudianteId: number, onSuccess: () => void) => {
  const [form, setForm]               = useState<MatriculaFormState>(MATRICULA_INITIAL);
  const [errors, setErrors]           = useState<MatriculaError>({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading]         = useState(true);
  const [matriculaActual, setMatriculaActual] = useState<MatriculaResponse | null>(null);

  // Ajuste de tarifas
  const [tarifaForm, setTarifaForm]         = useState<TarifaForm>({ costoMatricula: '', costoPension: '' });
  const [tarifaSubmitting, setTarifaSubmitting] = useState(false);
  const [tarifaError, setTarifaError]       = useState<string | null>(null);
  const [tarifaSuccess, setTarifaSuccess]   = useState(false);

  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [grados, setGrados]               = useState<Grado[]>([]);
  const [secciones, setSecciones]         = useState<Seccion[]>([]);

  // Descuentos asignados a la matrícula
  const [tiposDescuento, setTiposDescuento]   = useState<TipoDescuento[]>([]);
  const [descuentos, setDescuentos]           = useState<DescuentoEstudiante[]>([]);
  const [descuentoAddId, setDescuentoAddId]   = useState('');
  const [descuentoAdding, setDescuentoAdding] = useState(false);
  const [descuentoError, setDescuentoError]   = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [años, grs, secs, matricula, tipos] = await Promise.all([
          catalogoService.getAñosEscolares(),
          catalogoService.getGrados(),
          catalogoService.getSecciones(),
          matriculaService.getByEstudiante(estudianteId),
          tipoDescuentoService.listar(),
        ]);

        setAñosEscolares(años);
        setGrados(grs);
        setSecciones(secs);
        setTiposDescuento(tipos.filter(t => t.activo));

        if (matricula) {
          setMatriculaActual(matricula);
          setForm({
            anioEscolarId: String(matricula.anioEscolarId),
            gradoId:       String(matricula.gradoId),
            seccionId:     String(matricula.seccionId),
          });
          // Cargar descuentos asignados a esta matrícula
          const desc = await descuentoEstudianteService.listar(matricula.idMatricula);
          setDescuentos(desc);
        } else if (años.length > 0) {
          setForm(prev => ({ ...prev, anioEscolarId: String(años[0].idAnioEscolar) }));
        }
      } catch { /* no bloquear */ }
      finally { setLoading(false); }
    };
    init();
  }, [estudianteId]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const validate = (): MatriculaError => {
    const e: MatriculaError = {};
    if (!form.anioEscolarId) e.anioEscolarId = 'Requerido';
    if (!form.gradoId)       e.gradoId       = 'Requerido';
    if (!form.seccionId)     e.seccionId     = 'Requerido';
    return e;
  };

  const handleSubmit = useCallback(async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        estudianteId,
        gradoId:       Number(form.gradoId),
        seccionId:     Number(form.seccionId),
        anioEscolarId: Number(form.anioEscolarId),
      };
      if (matriculaActual) {
        await matriculaService.modificar(matriculaActual.idMatricula, payload);
      } else {
        await matriculaService.crear(payload);
      }
      onSuccess();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar matrícula');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, estudianteId, matriculaActual]);

  const [deleting, setDeleting]   = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleEliminar = useCallback(async () => {
    if (!matriculaActual) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await matriculaService.eliminar(matriculaActual.idMatricula);
      onSuccess();
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Error al eliminar matrícula');
    } finally {
      setDeleting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matriculaActual]);

  const handleTarifaChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTarifaForm(prev => ({ ...prev, [name]: value }));
    setTarifaError(null);
    setTarifaSuccess(false);
  }, []);

  const handleAjustarTarifas = useCallback(async () => {
    if (!matriculaActual) return;
    const costoMatricula = tarifaForm.costoMatricula !== '' ? Number(tarifaForm.costoMatricula) : null;
    const costoPension   = tarifaForm.costoPension   !== '' ? Number(tarifaForm.costoPension)   : null;

    if (costoMatricula === null && costoPension === null) {
      setTarifaError('Ingresa al menos un valor para ajustar');
      return;
    }
    if (costoMatricula !== null && costoMatricula <= 0) {
      setTarifaError('El costo de matrícula debe ser mayor a 0');
      return;
    }
    if (costoPension !== null && costoPension <= 0) {
      setTarifaError('El costo de pensión debe ser mayor a 0');
      return;
    }

    setTarifaSubmitting(true);
    setTarifaError(null);
    setTarifaSuccess(false);
    try {
      const updated = await matriculaService.ajustarTarifas(
        matriculaActual.idMatricula,
        { costoMatricula, costoPension }
      );
      setMatriculaActual(updated);
      setTarifaForm({ costoMatricula: '', costoPension: '' });
      setTarifaSuccess(true);
    } catch (err: unknown) {
      setTarifaError(err instanceof Error ? err.message : 'Error al ajustar tarifas');
    } finally {
      setTarifaSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matriculaActual, tarifaForm]);

  const handleAsignarDescuento = useCallback(async () => {
    if (!matriculaActual || !descuentoAddId) return;
    setDescuentoAdding(true);
    setDescuentoError(null);
    try {
      const nuevo = await descuentoEstudianteService.asignar({
        matriculaId:     matriculaActual.idMatricula,
        tipoDescuentoId: Number(descuentoAddId),
      });
      setDescuentos(prev => [...prev, nuevo]);
      setDescuentoAddId('');
    } catch (err: unknown) {
      setDescuentoError(err instanceof Error ? err.message : 'Error al asignar descuento');
    } finally {
      setDescuentoAdding(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matriculaActual, descuentoAddId]);

  const handleEliminarDescuento = useCallback(async (idDescuentoEstudiante: number) => {
    setDescuentoError(null);
    try {
      await descuentoEstudianteService.eliminar(idDescuentoEstudiante);
      setDescuentos(prev => prev.filter(d => d.idDescuentoEstudiante !== idDescuentoEstudiante));
    } catch (err: unknown) {
      setDescuentoError(err instanceof Error ? err.message : 'Error al eliminar descuento');
    }
  }, []);

  return {
    form, errors, submitting, submitError, loading,
    matriculaActual, añosEscolares, grados, secciones,
    handleChange, handleSubmit,
    tarifaForm, tarifaSubmitting, tarifaError, tarifaSuccess,
    handleTarifaChange, handleAjustarTarifas,
    deleting, deleteError, handleEliminar,
    tiposDescuento, descuentos, descuentoAddId, setDescuentoAddId,
    descuentoAdding, descuentoError,
    handleAsignarDescuento, handleEliminarDescuento,
  };
};